import Vue from "vue";

export enum RecursionDepthType {
    Deep = 1,
    Shallow = 2,
    StopAtTranslation = 3
}

let recursionDepthDefault: RecursionDepthType = RecursionDepthType.StopAtTranslation;

export function setRecursionDepthDefault(depth: RecursionDepthType) {
    recursionDepthDefault = depth;
}

export interface IMappingTranslators {
    [key: string]: (data?: any) => any; // todo - is there a type that specifies a class instance? InstanceType?
}

function clone(src: any, target: any, translators: IMappingTranslators, recursionDepth: RecursionDepthType) {

    // do we have an array wrapper with a translator function?
    const arrayTranslator = src != null && typeof src._translator === "function"
        ? src._translator
        : null;
    // ...if so, unwrap
    if(arrayTranslator !== null) src = src._array;

    // process all of the properties or array elements
    // (so i can be property name OR array index)
    for (const i in src) {
        if (!src.hasOwnProperty(i)) continue;
        const type = typeof src[i];

        if (Array.isArray(src[i])) {
            const translator = translators[i];
            Vue.set(target, i, []);
            if(typeof translator === "function") {
                const arrayWrapper = {
                    _translator: translator,
                    _array: src[i]
                };
                clone(arrayWrapper, target[i], translators, recursionDepth);
            }
            else {
                clone(src[i], target[i], translators, recursionDepth);
            }
        }
        else if (src[i] === null) {
            // because typeof null === "object"
            Vue.set(target, i, null);
        }
        else if (Object.prototype.toString.call(src[i]) === "[object Date]") {
            Vue.set(target, i, new Date(src[i].getTime()));
        }
        else if (type === "object") {

            // translator function can come from options or from arrayWrapper (parent, i.e. src, is array)
            const translator = translators[i] || arrayTranslator;

            //console.log(`🗝️ key = ${i} | depth = `, RecursionDepthType[recursionDepth]);

            switch(recursionDepth) {
                case RecursionDepthType.Deep:
                    Vue.set(target, i, typeof(translator) === "function"
                        ? translator(src[i])
                        : {});
                    clone(src[i], target[i], translators, recursionDepth);
                    break;

                case RecursionDepthType.StopAtTranslation:
                    if(typeof(translator) === "function") {
                        Vue.set(target, i, translator(src[i]));
                        // do not carry on recursion
                    }
                    else {
                        Vue.set(target, i, {});
                        clone(src[i], target[i], translators, recursionDepth);
                    }
                    break;

                default:
                    // shallow clone - if there's an Object property, just copy it (as though we'd used Object.assign)
                    Vue.set(target, i, src[i]);
              }
        }
        else {
            Vue.set(target, i, src[i]);
        }
    }
}

export function mapData<T>(source: Record<string, any>, options: IMappingTranslators, recursionDepth: RecursionDepthType = recursionDepthDefault): T {
    if(!options) {
        throw "options not supplied!";
    }
    if(typeof options.root === "function") {
        if(Array.isArray(source)) {
            const translator = options.root;
            const target: Array<any> = [];
            const arrayWrapper = {
                _translator: translator,
                _array: source
            };
            clone(arrayWrapper, target, options, recursionDepth);
            return (target as unknown) as T;
        }
        else {
            const target = options.root(source);
            clone(source, target, options, recursionDepth);
            return target as T;
        }
    }
    else {
        console.warn("root was not specified in options!");
        // create empty object or array as target as fallback
        const target = Array.isArray(source) ? [] : {};
        clone(source, target, options, recursionDepth);
        return target as T;
    }
}
